import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getTradesList, deleteTrade } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const TradesList = () => {
    const [trades, setTrades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        status: 'all',
        type: 'all',
        userId: '',
        page: 1,
        limit: 10
    });
    const [pagination, setPagination] = useState({
        page: 1,
        total: 0,
        pages: 1
    });

    const fetchTrades = async () => {
        try {
            setLoading(true);
            const params = {
                page: filters.page,
                limit: filters.limit,
                ...(filters.status !== 'all' && { status: filters.status }),
                ...(filters.type !== 'all' && { type: filters.type }),
                ...(filters.userId && { userId: filters.userId })
            };
            const response = await getTradesList(params);
            
            if (response.success) {
                setTrades(response.data?.trades || response.data || []);
                setPagination(response.data?.pagination || { page: 1, total: 0, pages: 1 });
            } else {
                throw new Error(response.message || 'Failed to fetch trades');
            }
        } catch (error) {
            console.error('Error fetching trades:', error);
            setError(error.message || 'Failed to fetch trades');
            setTrades([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTrades();
    }, [filters.page, filters.status, filters.type, filters.userId]);

    const handleDelete = async (tradeId) => {
        if (!window.confirm('Are you sure you want to delete this trade?')) return;
        
        try {
            await deleteTrade(tradeId);
            alert('Trade deleted successfully');
            fetchTrades();
        } catch (error) {
            alert(error.message || 'Failed to delete trade');
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: 'success',
            closed: 'secondary',
            cancelled: 'danger'
        };
        return badges[status] || 'secondary';
    };

    if (loading) {
        return (
            <div className="container-xxl">
                <div className="d-flex justify-content-center align-items-center min-vh-50">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Trades List" sidebutton={true} />
            <div className="row">
                <div className="col-lg-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <Card.Title>Trades</Card.Title>
                            <Link to="/admin/manual-trades" className="btn btn-primary">
                                Create Manual Trade
                            </Link>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={3}>
                                    <Form.Select
                                        value={filters.status}
                                        onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="closed">Closed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Select
                                        value={filters.type}
                                        onChange={(e) => setFilters({ ...filters, type: e.target.value, page: 1 })}
                                    >
                                        <option value="all">All Types</option>
                                        <option value="forex">Forex</option>
                                        <option value="crypto">Crypto</option>
                                        <option value="gold">Gold</option>
                                    </Form.Select>
                                </Col>
                            </Row>
                            {error && <div className="alert alert-danger">{error}</div>}
                            <div className="table-responsive">
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Trade ID</th>
                                            <th>User</th>
                                            <th>Symbol</th>
                                            <th>Type</th>
                                            <th>Direction</th>
                                            <th>Entry Price</th>
                                            <th>Close Price</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trades.length === 0 ? (
                                            <tr>
                                                <td colSpan="9" className="text-center">No trades found</td>
                                            </tr>
                                        ) : (
                                            trades.map((trade) => (
                                                <tr key={trade._id}>
                                                    <td>{trade._id?.substring(0, 8)}...</td>
                                                    <td>{trade.user?.name || trade.user?.email || '-'}</td>
                                                    <td>{trade.symbol?.name || trade.symbol || '-'}</td>
                                                    <td>
                                                        <Badge bg="info">{trade.type || '-'}</Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg={trade.direction === 'buy' ? 'success' : 'danger'}>
                                                            {trade.direction?.toUpperCase() || '-'}
                                                        </Badge>
                                                    </td>
                                                    <td>${trade.entryPrice?.toFixed(2) || '-'}</td>
                                                    <td>${trade.closePrice?.toFixed(2) || '-'}</td>
                                                    <td>
                                                        <Badge bg={getStatusBadge(trade.status)}>
                                                            {trade.status || '-'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Link 
                                                            to={`/admin/view-trade/${trade._id}`} 
                                                            className="btn btn-sm btn-primary me-1"
                                                        >
                                                            View
                                                        </Link>
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(trade._id)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                            {pagination.pages > 1 && (
                                <div className="d-flex justify-content-center mt-3">
                                    <Button
                                        variant="outline-primary"
                                        disabled={pagination.page === 1}
                                        onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
                                    >
                                        Previous
                                    </Button>
                                    <span className="mx-3 align-self-center">
                                        Page {pagination.page} of {pagination.pages}
                                    </span>
                                    <Button
                                        variant="outline-primary"
                                        disabled={pagination.page >= pagination.pages}
                                        onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
                                    >
                                        Next
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default TradesList;